import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync } from '@fortawesome/free-solid-svg-icons';
import ChannelsService from '../../services/ChannelsService';
import { Loading } from '../Loading';
import ConfirmationModal from '../ConfirmationModal';
import { ChannelsTable } from './ChannelsTable';
import { ChannelsFilters } from './ChannelsFilters';
import { ChannelModal } from './ChannelModal';

const EMPTY_FILTERS = { q: '', content: '', active: '', client_type: '', distribution: '' };

export function ChannelsAdminPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');

    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const [modal, setModal] = useState({ open: false, isNew: true, channel: null });
    const [toggleTarget, setToggleTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchChannels = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await ChannelsService.getChannels(filters);
            setItems(res.channels || []);
            setTotal(res.total || 0);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchChannels();
        return () => { setItems([]); };
    }, [fetchChannels, refreshKey]);

    const applyFilter = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setRefreshKey((k) => k + 1);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setRefreshKey((k) => k + 1);
    };

    const flash = (msg) => {
        setFeedback(msg);
        setTimeout(() => setFeedback(''), 4000);
    };

    const openCreate = () => setModal({ open: true, isNew: true, channel: null });
    const openEdit = (ch) => setModal({ open: true, isNew: false, channel: ch });

    const handleSave = async (payload) => {
        setSaving(true);
        try {
            const res = await ChannelsService.setChannel(payload);
            setRefreshKey((k) => k + 1);
            if (modal.isNew) {
                flash('Canal creado. Completa las demás secciones desde Editar.');
                const created = {
                    ...payload,
                    _id: res._id,
                    active: res.active !== undefined ? res.active : false,
                    is_complete: !!res.is_complete,
                    missing_fields: res.missing_fields || [],
                };
                setModal({ open: true, isNew: false, channel: created });
            } else {
                flash('Canal guardado correctamente');
                setModal({ open: false, isNew: true, channel: null });
            }
            return { ok: true };
        } catch (e) {
            return { ok: false, msg: e.message };
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmToggle = async () => {
        if (!toggleTarget) return;
        if (!toggleTarget.active && !toggleTarget.is_complete) {
            setToggleTarget(null);
            setError('Completa la configuración del canal antes de activarlo.');
            return;
        }
        setSaving(true);
        try {
            await ChannelsService.setChannel({ _id: toggleTarget._id, active: !toggleTarget.active });
            setToggleTarget(null);
            flash(toggleTarget.active ? 'Canal desactivado' : 'Canal activado');
            setRefreshKey((k) => k + 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        setSaving(true);
        try {
            await ChannelsService.deleteChannel(deleteTarget._id);
            setDeleteTarget(null);
            flash('Canal eliminado');
            setRefreshKey((k) => k + 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {error && (
                <div className="alert alert-danger py-2">{error}
                    <button className="btn-close ms-2 float-end" onClick={() => setError('')} />
                </div>
            )}
            {feedback && (
                <div className="alert alert-success py-2">{feedback}
                    <button className="btn-close ms-2 float-end" onClick={() => setFeedback('')} />
                </div>
            )}

            <ChannelsFilters
                filters={filters}
                onChange={applyFilter}
                onSearch={() => setRefreshKey((k) => k + 1)}
                onReset={handleReset}
            />

            <div className="row mb-3 mt-4">
                <div className="col-md-12 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-uppercase text-white opacity-75">{total} canal(es)</span>
                    <div className="d-flex gap-2">
                        <button className="btn btn-ink-icon" onClick={() => setRefreshKey(k => k + 1)} title="Recargar lista">
                            <FontAwesomeIcon icon={faSync} spin={loading} />
                        </button>
                        <button className="btn btn-success px-3" onClick={openCreate} title="Crear canal">
                            <FontAwesomeIcon icon={faPlus} className="me-1" /> Nuevo canal
                        </button>
                    </div>
                </div>
            </div>

            {loading ? <Loading /> : (items.length === 0 ? (
                <div className="text-center text-white-50 py-5">No hay canales que coincidan con los filtros.</div>
            ) : (
                <ChannelsTable
                    channels={items}
                    onEdit={openEdit}
                    onToggleActive={(ch) => setToggleTarget(ch)}
                    onDelete={(ch) => setDeleteTarget(ch)}
                />
            ))}

            <ChannelModal
                isOpen={modal.open}
                onClose={() => setModal({ open: false, isNew: true, channel: null })}
                channel={modal.channel}
                isNew={modal.isNew}
                onSave={handleSave}
                saving={saving}
            />

            <ConfirmationModal
                isOpen={toggleTarget !== null}
                onClose={() => setToggleTarget(null)}
                onConfirm={handleConfirmToggle}
                title={toggleTarget ? (toggleTarget.active ? 'Desactivar canal' : 'Activar canal') : ''}
                message={toggleTarget ? `¿Estás seguro de ${toggleTarget.active ? 'desactivar' : 'activar'} el canal "${toggleTarget.name || toggleTarget.alias}"?` : ''}
                confirmText={toggleTarget ? (toggleTarget.active ? 'Desactivar' : 'Activar') : ''}
                type="warning"
                isLoading={saving}
            />

            <ConfirmationModal
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title="Eliminar canal"
                message={`Se eliminará el canal "${deleteTarget ? (deleteTarget.name || deleteTarget.alias) : ''}". Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
                type="danger"
                isLoading={saving}
            />
        </>
    );
}

export default ChannelsAdminPage;